import {
  Fragment,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { useResizeObserver } from '@react-hookz/web';
import { Switch } from 'src/components/ui/switch';
import { Label } from 'src/components/ui/label';
import {
  useSessionStatus,
  type ScreenshotEntry,
  type SessionMessageEntry,
} from 'src/api/rpc/hooks/agent/useAgentQueries';
import { useOperatorSessionStream } from 'src/api/rpc/hooks/agent/useOperatorSessionStream';
import { useGetDocument } from 'src/api/core/documents/useGetDocument';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { ScrollArea } from 'src/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from 'src/components/ui/tabs';
import {
  ArrowsOutSimpleIcon,
  CircleNotchIcon,
  CheckCircleIcon,
  XCircleIcon,
  CameraIcon,
  CaretDownIcon,
  CaretRightIcon,
  ListChecksIcon,
  MonitorIcon,
  SignInIcon,
  GlobeIcon,
  GraduationCapIcon,
  CursorClickIcon,
} from '@phosphor-icons/react';
import { cn } from 'src/lib/utils';
import type {
  AgentSessionStatus,
  SopReplayStepView,
  SopReplayRunningStep,
  ToolCallRunResponse,
} from '@erp/rpc';
import config from 'src/config';
import { T, Var, useGT } from 'gt-react';
import {
  SessionVideoPlayer,
  ClickRipple,
  computeContainedScale,
  isClickAction,
  formatActionLabel,
  type VideoScale,
  type VideoActionMarker,
} from 'src/components/common/SessionVideoPlayer';
import { ChatMarkdown } from 'src/components/common/chat';
import {
  buildScreenshotItems,
  buildToolCallMessages,
  extractActionData,
  screenshotMatchesSelection,
  selectScreenshotItems,
  type ScreenshotItem,
} from 'src/components/common/agentSessionScreenshots';
import {
  useAgentSessionNavigation,
  type AgentSessionTab,
} from 'src/components/common/useAgentSessionNavigation';
import {
  defaultAgentSessionTab,
  resolveAgentSessionTab,
  shouldRenderAgentSessionViewer,
} from 'src/components/common/agentSessionTabs';
import { KernelTelemetryEventsPanel } from 'src/components/common/KernelTelemetryEventsPanel';
import {
  hasKernelTelemetry,
  notableKernelTelemetryCount,
} from 'src/components/common/kernelTelemetryDisplay';
import type { KernelTelemetrySummary } from '@erp/rpc';
import {
  actionIcon,
  actionDetail,
  entryTime,
} from 'src/components/common/agentActionFormatting';
import {
  SOP_MODE_BADGE_VARIANT,
  sopVerifyState,
  stepModeLabel,
} from './sopReplayCore';
import {
  groupTraceBySopStep,
  sopStepSegments,
  type PhaseGroup,
  type ProcedureGroup,
  type SopStepGroup,
  type TraceGroup,
} from 'src/components/common/agentTraceGrouping';

// ---------------------------------------------------------------------------
// Scroll helpers — operate on the Radix ScrollArea viewport directly so we
// never trigger scrollIntoView's ancestor-walking (which scrolls the page).
// ---------------------------------------------------------------------------

function getScrollViewport(container: HTMLElement | null): HTMLElement | null {
  return (
    container?.querySelector<HTMLElement>(
      '[data-slot="scroll-area-viewport"]',
    ) ?? null
  );
}

// Nudge a scroll viewport just enough to reveal `target`, leaving the page
// scroll untouched — the manual alternative to scrollIntoView, which walks
// every scrollable ancestor including the document.
function keepInViewport(
  viewport: HTMLElement | null,
  target: HTMLElement | null,
): void {
  if (!viewport || !target) return;
  const vp = viewport.getBoundingClientRect();
  const t = target.getBoundingClientRect();
  if (t.top < vp.top) {
    viewport.scrollTop -= vp.top - t.top;
  } else if (t.bottom > vp.bottom) {
    viewport.scrollTop += t.bottom - vp.bottom;
  }
}

// The ScrollArea viewport this element sits INSIDE (an ancestor lookup — the
// mirror of getScrollViewport's descendant one). Null when the element isn't in
// a ScrollArea, in which case nothing may be scrolled on its behalf.
function enclosingScrollViewport(el: HTMLElement | null): HTMLElement | null {
  return el?.closest<HTMLElement>('[data-slot="scroll-area-viewport"]') ?? null;
}

// Keeps the active thumbnail visible as the selection or item count changes.
// Following the *active* index (rather than blindly sticking to the bottom)
// works for both the live strip (active = latest, so it follows new frames) and
// a completed run's gallery (active = whatever the user clicked) without the
// list jumping around as screenshots stream in.
function useKeepActiveThumbInView(activeIndex: number, itemCount: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeThumbRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    keepInViewport(
      getScrollViewport(containerRef.current),
      activeThumbRef.current,
    );
  }, [activeIndex, itemCount]);
  return { containerRef, activeThumbRef };
}

// ---------------------------------------------------------------------------
// Screenshot gallery sub-component (shared by every tab that shows screenshots)
// ---------------------------------------------------------------------------

export function ScreenshotGallery({
  items,
  selectedIndex,
  onSelect,
}: {
  items: ScreenshotItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const clampedIndex =
    items.length > 0 ? Math.min(selectedIndex, items.length - 1) : 0;
  // Keep the active thumbnail visible — e.g. when clicking a thread message
  // jumps the gallery to a frame that's scrolled out of the sidebar — without
  // scrolling the page.
  const { containerRef: thumbStripRef, activeThumbRef } =
    useKeepActiveThumbInView(clampedIndex, items.length);

  if (items.length === 0) return null;

  const current = items[clampedIndex];

  return (
    <>
      <div className="flex gap-3 items-start relative">
        {/* Thumbnail sidebar */}
        {items.length > 1 && (
          <>
            <div className="w-32" />
            <div ref={thumbStripRef} className="w-32 absolute h-full">
              <ScrollArea className="h-full">
                <div className="flex flex-col gap-1.5 pr-2">
                  {items.map((item, index) => {
                    const isActive = index === clampedIndex;
                    return (
                      <button
                        type="button"
                        key={item.id}
                        ref={isActive ? activeThumbRef : null}
                        onClick={() => onSelect(index)}
                        className={cn(
                          'relative rounded border overflow-hidden cursor-pointer transition-all',
                          'hover:ring-2 hover:ring-primary/50',
                          isActive
                            ? 'ring-2 ring-primary border-primary'
                            : 'border-border',
                        )}
                      >
                        <img
                          src={item.src}
                          alt={item.label}
                          className="w-full h-auto"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                          <span className="text-[9px] text-white truncate block">
                            {item.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </>
        )}

        {/* Main screenshot */}
        <div className="flex-1 min-w-0">
          {current && (
            <div className="space-y-1">
              <ActionOverlayImage
                src={current.src}
                alt={current.label}
                className="rounded border overflow-hidden block w-full hover:ring-2 hover:ring-primary/50 cursor-pointer"
                imgClassName="w-full h-auto"
                coordinate={current.coordinate}
                actionType={current.actionType}
                actionText={current.actionText}
                onClick={() => setIsDialogOpen(true)}
              />
              <div className="text-xs text-muted-foreground text-center">
                {current.label}
                {items.length > 1 && ` (${clampedIndex + 1}/${items.length})`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full-size dialog */}
      {current && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="!max-w-[min(95vw,72rem)] sm:!max-w-[min(95vw,72rem)] max-h-[95vh] p-4">
            <DialogHeader>
              <DialogTitle>
                <T context="agent run screenshot dialog title">
                  Screenshot: <Var>{current.label}</Var>
                </T>
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[80vh] overflow-y-auto rounded border">
              <ActionOverlayImage
                src={current.src}
                alt={current.label}
                imgClassName="w-full h-auto"
                coordinate={current.coordinate}
                actionType={current.actionType}
                actionText={current.actionText}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Status badge config
// ---------------------------------------------------------------------------

function useStatusConfig() {
  const gt = useGT();
  return useMemo<
    Record<
      AgentSessionStatus,
      { label: string; color: string; icon: React.ReactNode }
    >
  >(
    () => ({
      pending: {
        label: gt('Pending', { context: 'agent session status' }),
        color: 'bg-gray-500',
        icon: <CircleNotchIcon className="animate-spin" />,
      },
      starting: {
        label: gt('Starting...', { context: 'agent session status' }),
        color: 'bg-blue-500',
        icon: <CircleNotchIcon className="animate-spin" />,
      },
      connecting: {
        label: gt('Connecting...', { context: 'agent session status' }),
        color: 'bg-blue-500',
        icon: <CircleNotchIcon className="animate-spin" />,
      },
      authenticating: {
        label: gt('Authenticating...', { context: 'agent session status' }),
        color: 'bg-yellow-500',
        icon: <CircleNotchIcon className="animate-spin" />,
      },
      mfa: {
        label: gt('MFA...', { context: 'agent session status' }),
        color: 'bg-yellow-500',
        icon: <CircleNotchIcon className="animate-spin" />,
      },
      connected: {
        label: gt('Connected', { context: 'agent session status' }),
        color: 'bg-green-500',
        icon: <CircleNotchIcon className="animate-spin" />,
      },
      running: {
        label: gt('Running...', { context: 'agent session status' }),
        color: 'bg-green-500',
        icon: <CircleNotchIcon className="animate-spin" />,
      },
      completed: {
        label: gt('Completed', { context: 'agent session status' }),
        color: 'bg-green-600',
        icon: <CheckCircleIcon weight="fill" />,
      },
      failed: {
        label: gt('Failed', { context: 'agent session status' }),
        color: 'bg-red-600',
        icon: <XCircleIcon weight="fill" />,
      },
      cancelled: {
        label: gt('Cancelled', { context: 'agent session status' }),
        color: 'bg-gray-600',
        icon: <XCircleIcon />,
      },
      expired: {
        label: gt('Session Expired', { context: 'agent session status' }),
        color: 'bg-gray-600',
        icon: <XCircleIcon />,
      },
    }),
    [gt],
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatActionName(
  action: string | undefined,
  gt: ReturnType<typeof useGT>,
): string {
  if (!action)
    return gt('Screenshot', {
      context: 'default label when action name missing',
    });
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Action overlay helpers — VideoScale / computeContainedScale / isClickAction /
// formatActionLabel / ClickRipple now live in SessionVideoPlayer (shared).
// actionIcon / actionDetail / entryTime now live in agentActionFormatting
// (shared with the operator interview's turn cards).
// ---------------------------------------------------------------------------
// Structured answer rendering — when a thread entry's whole text is a JSON
// object (the operator's structured final answer for evals that expect one),
// show it as a labelled key/value card instead of a raw JSON blob.
// ---------------------------------------------------------------------------

/** Parse text that is *entirely* a JSON object; null for prose or anything else. */
export function parseStructured(text: string): Record<string, unknown> | null {
  const t = text.trim();
  if (!t.startsWith('{') || !t.endsWith('}')) return null;
  try {
    const v: unknown = JSON.parse(t);
    return v && typeof v === 'object' && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** `snake_case`/`camelCase` field key → "Title case" label. */
function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

function isEmptyValue(v: unknown): boolean {
  return (
    v === null ||
    v === undefined ||
    v === '' ||
    (Array.isArray(v) && v.length === 0)
  );
}

function StructuredValue({ value }: { value: unknown }) {
  if (isEmptyValue(value))
    return <span className="text-muted-foreground">—</span>;
  if (Array.isArray(value))
    return (
      <ul className="list-disc space-y-0.5 pl-4">
        {value.map((v, i) => (
          <li key={i}>
            <StructuredValue value={v} />
          </li>
        ))}
      </ul>
    );
  if (typeof value === 'object')
    return <StructuredObject data={value as Record<string, unknown>} />;
  if (typeof value === 'boolean')
    return (
      <span>
        {value ? (
          <T context="boolean value true">Yes</T>
        ) : (
          <T context="boolean value false">No</T>
        )}
      </span>
    );
  return <span className="break-words">{String(value)}</span>;
}

export function StructuredObject({ data }: { data: Record<string, unknown> }) {
  // Drop empty/null fields (e.g. notes: null) so the card shows only real values.
  const entries = Object.entries(data).filter(([, v]) => !isEmptyValue(v));
  if (entries.length === 0)
    return <span className="text-muted-foreground">—</span>;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[13px]">
      {entries.map(([k, v]) => (
        <Fragment key={k}>
          <dt className="font-medium text-muted-foreground">
            {humanizeKey(k)}
          </dt>
          <dd className="min-w-0 text-foreground">
            <StructuredValue value={v} />
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * One entry in the agent thread. A tool call with no extra detail renders as a
 * compact icon + label row; reasoning prose (and legacy action+text) renders as
 * a card.
 */
function AgentThreadEntry({
  message,
  gt,
  onSelectScreenshot,
  isSelected = false,
  showDetail = false,
}: {
  message: SessionMessageEntry;
  gt: ReturnType<typeof useGT>;
  /** Present when this message has a screenshot to reveal; click → reveal it. */
  onSelectScreenshot?: () => void;
  /** Its screenshot is the one currently shown — highlight + scroll into view. */
  isSelected?: boolean;
  /** Render the per-action detail badge (key/text/scroll/coordinate). Flag-gated. */
  showDetail?: boolean;
}) {
  const action = message.action;
  const detail = showDetail ? actionDetail(action, message.metadata) : null;
  const hasText = Boolean(message.message?.trim());
  // A JSON-object message is the operator's structured final answer — render it
  // as a labelled key/value card, not a raw-JSON "Reasoning" blob.
  const structured = hasText ? parseStructured(message.message ?? '') : null;
  // No timestamp (eval turns carry none) → render the row without a time.
  const time = message.timestamp ? entryTime(message.timestamp) : null;
  const clickable = Boolean(onSelectScreenshot);
  const rootRef = useRef<HTMLDivElement>(null);

  // Reveal the selected entry inside the Trajectory thread ONLY. scrollIntoView
  // here walked up to the document and yanked the whole page down to the bottom
  // of the viewer every time a screenshot was clicked.
  useEffect(() => {
    if (!isSelected) return;
    keepInViewport(enclosingScrollViewport(rootRef.current), rootRef.current);
  }, [isSelected]);

  // Nothing to render — skip empty entries (no action, no text) so the thread
  // never shows a bare "Reasoning" block. Keep clickable ones (a screenshot to reveal).
  if (!action && !hasText && !clickable) return null;

  // A11y/affordance: an entry tied to a screenshot becomes a button. Keep the
  // visual identical to the non-clickable entry, just add a hover ring + camera,
  // and a solid ring when its screenshot is the selected one.
  function entryProps(base: string) {
    const className = cn(
      base,
      clickable &&
        'w-full text-left cursor-pointer rounded-md hover:ring-2 hover:ring-primary/40',
      isSelected && 'ring-2 ring-primary bg-primary/5',
    );
    if (!clickable) return { className };
    return {
      className,
      role: 'button' as const,
      tabIndex: 0,
      onClick: onSelectScreenshot,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelectScreenshot?.();
        }
      },
      title: gt('View screenshot', {
        context:
          'tooltip for a clickable agent thread entry that has a screenshot',
      }),
    };
  }

  if (action && !hasText) {
    const ActionIcon = actionIcon(action);
    // className hoisted to a const so i18next/no-literal-string (jsx-only) doesn't
    // flag a Tailwind class string passed through entryProps().
    const rowClassName = 'flex items-center gap-2 px-1 py-1 text-[13px]';
    return (
      <div ref={rootRef} {...entryProps(rowClassName)}>
        <ActionIcon
          weight="bold"
          className="h-4 w-4 shrink-0 text-muted-foreground"
        />
        <span className="font-medium text-foreground">
          {formatActionName(action, gt)}
        </span>
        {detail && (
          <code className="min-w-0 truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/80">
            {detail}
          </code>
        )}
        {clickable && (
          <CameraIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
        )}
        {time && (
          <time
            dateTime={new Date(message.timestamp).toISOString()}
            className="ml-auto shrink-0 text-[11px] text-muted-foreground tabular-nums"
          >
            {time}
          </time>
        )}
      </div>
    );
  }

  const HeaderIcon = action
    ? actionIcon(action)
    : structured
      ? CheckCircleIcon
      : null;
  const cardClassName = 'rounded-md bg-muted/40 p-2';
  return (
    <div ref={rootRef} {...entryProps(cardClassName)}>
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5 truncate">
          {HeaderIcon && <HeaderIcon weight="bold" className="h-3.5 w-3.5" />}
          <span className="truncate font-medium">
            {action
              ? formatActionName(action, gt)
              : structured
                ? gt('Final answer', {
                    context:
                      "label for the operator's structured final-answer entry",
                  })
                : // A replay step's own frame is a picture, not a thought. It reaches here with
                  // no action (the engine's internal marker is stripped) and no text (that text
                  // only restated the step's row), so calling it "Reasoning" claims the agent
                  // deliberated over a step it may have skipped outright.
                  !hasText && clickable
                  ? gt('Screenshot', {
                      context:
                        'label for a trace entry that is only a captured frame',
                    })
                  : gt('Reasoning', {
                      context: 'label for an agent reasoning thought entry',
                    })}
          </span>
          {detail && (
            <code className="min-w-0 truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground/80">
              {detail}
            </code>
          )}
          {clickable && (
            <CameraIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
          )}
        </span>
        {time && (
          <time
            dateTime={new Date(message.timestamp).toISOString()}
            className="shrink-0 tabular-nums"
          >
            {time}
          </time>
        )}
      </div>
      {structured ? (
        <StructuredObject data={structured} />
      ) : (
        hasText && (
          <ChatMarkdown className="text-foreground [&_p]:leading-5">
            {message.message ?? ''}
          </ChatMarkdown>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SOP replay grouping — a procedure's steps as containers for the operator
// actions they produced (see agentTraceGrouping.ts for the pure fold).
// ---------------------------------------------------------------------------

/** The ✓/✗ for a machine-verified step. `role="img"` + `aria-label` rather than a bare `title`, so
 * the verdict reaches screen readers and touch users too. */
function StepVerifyBadge({
  state,
  gt,
}: {
  state: 'held' | 'failed';
  gt: ReturnType<typeof useGT>;
}) {
  const label =
    state === 'held'
      ? gt('Verify post-condition held', {
          context: 'label: the replay step’s Verify check passed',
        })
      : gt('Verify post-condition did not hold', {
          context: 'label: the replay step’s Verify check failed',
        });
  return (
    <span role="img" aria-label={label} title={label} className="shrink-0">
      {state === 'held' ? (
        <CheckCircleIcon
          weight="fill"
          className="h-3.5 w-3.5 text-green-600 dark:text-green-500"
          aria-hidden
        />
      ) : (
        <XCircleIcon
          weight="fill"
          className="h-3.5 w-3.5 text-destructive"
          aria-hidden
        />
      )}
    </span>
  );
}

/** Should this step start expanded? A clean deterministic replay is noise — one row says it all —
 * but a step the engine could not simply reproduce is exactly what someone opened the trace to read,
 * so it must never be hidden behind a caret. `agentic` joins `healed` and `failed`: it means the
 * step had no code to replay and an agent performed it instead, which is the same "why did this not
 * just run?" question. */
function stepDefaultsExpanded(step: SopReplayStepView): boolean {
  return (
    step.mode === 'healed' || step.mode === 'failed' || step.mode === 'agentic'
  );
}

/** One replayed step: a single row when it went cleanly, expandable into the operator actions it
 * produced. */
function SopStepRow({
  group,
  gt,
  entryProps,
}: {
  group: SopStepGroup;
  gt: ReturnType<typeof useGT>;
  /** Renders one nested action row — supplied by the thread so screenshot linking still works. */
  entryProps: (message: SessionMessageEntry) => React.ReactNode;
}) {
  const { step, messages } = group;
  const [overridden, setOverridden] = useState<boolean | null>(null);
  const expanded = overridden ?? stepDefaultsExpanded(step);
  const canExpand = messages.length > 0;
  const verify = sopVerifyState(step.verify);

  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-2 py-1 text-[13px]">
        {canExpand ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
            onClick={() => setOverridden(!expanded)}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? gt('Collapse step', {
                    context: 'collapse a procedure step’s actions in the trace',
                  })
                : gt('Expand step', {
                    context: 'expand a procedure step’s actions in the trace',
                  })
            }
          >
            {expanded ? (
              <CaretDownIcon className="h-3.5 w-3.5" />
            ) : (
              <CaretRightIcon className="h-3.5 w-3.5" />
            )}
          </Button>
        ) : (
          <span className="w-5 shrink-0" />
        )}
        <span className="w-5 shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums">
          {step.index}
        </span>
        <span className="min-w-0 flex-1 truncate font-medium text-foreground">
          {step.name}
        </span>
        {canExpand && (
          // What's behind the caret, so a collapsed step still says whether it did anything.
          <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
            {messages.length}
          </span>
        )}
        <Badge
          variant={step.noop ? 'outline' : SOP_MODE_BADGE_VARIANT[step.mode]}
          className="shrink-0 text-[10px]"
        >
          {stepModeLabel(step)}
        </Badge>
        {verify && <StepVerifyBadge state={verify} gt={gt} />}
      </div>
      {step.error && (
        // Why the step couldn't be reproduced — inline, so a failed step says more than its badge.
        <p className="pl-12 text-[11px] break-words text-destructive">
          {step.error}
        </p>
      )}
      {expanded && canExpand && (
        <div className="ml-7 space-y-1 border-l pl-2">
          {messages.map((message) => entryProps(message))}
        </div>
      )}
    </div>
  );
}

/** One procedure replay: the envelope, its steps, and the step currently mid-replay. */
function ProcedureGroupCard({
  group,
  gt,
  entryProps,
}: {
  group: ProcedureGroup;
  gt: ReturnType<typeof useGT>;
  entryProps: (message: SessionMessageEntry) => React.ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const healed = group.steps.filter((s) => s.step.mode === 'healed').length;
  const failed = group.steps.filter((s) => s.step.mode === 'failed').length;
  const skipped = group.steps.filter((s) => s.step.noop).length;
  // Null unless this plan composed several SOPs via `sop_ref` — the flat list stays flat.
  const segments = sopStepSegments(group.steps);

  return (
    <div className="rounded-md border bg-muted/20">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
        className="flex w-full min-w-0 items-center gap-2 px-2 py-1.5 text-left text-[13px] hover:bg-muted/40"
      >
        {collapsed ? (
          <CaretRightIcon className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <CaretDownIcon className="h-3.5 w-3.5 shrink-0" />
        )}
        <ListChecksIcon
          weight="bold"
          className="h-4 w-4 shrink-0 text-muted-foreground"
        />
        <span className="min-w-0 flex-1 truncate font-medium">
          {group.name ??
            gt('Procedure', {
              context:
                'fallback label for a replayed procedure with no name on the wire',
            })}
        </span>
        <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
          {group.runningStep ? (
            // Mid-replay the resolved count alone undercounts what's on screen: the in-progress
            // step renders its own row but isn't in `steps` yet, so a run showing five rows read
            // "4 steps". Its own index, not `steps.length + 1` — a replay that skipped an
            // unchosen branch path resolves fewer steps than it has walked past.
            <T context="which step of a procedure replay is currently running">
              step <Var>{group.runningStep.index}</Var>
            </T>
          ) : (
            <T context="summary of how many steps a procedure replay ran">
              <Var>{group.steps.length}</Var> steps
            </T>
          )}
        </span>
        {failed > 0 ? (
          <Badge variant="destructive" className="shrink-0 text-[10px]">
            <T context="badge counting failed steps in a procedure replay">
              <Var>{failed}</Var> failed
            </T>
          </Badge>
        ) : healed > 0 ? (
          <Badge variant="warning" className="shrink-0 text-[10px]">
            <T context="badge counting healed steps in a procedure replay">
              <Var>{healed}</Var> healed
            </T>
          </Badge>
        ) : skipped > 0 ? (
          // Only when nothing drifted: a replay can report success having filled nothing, so a
          // clean-looking run still has to declare how many optional stages it walked past.
          <Badge variant="outline" className="shrink-0 text-[10px]">
            <T context="badge counting skipped optional steps in a procedure replay">
              <Var>{skipped}</Var> skipped
            </T>
          </Badge>
        ) : null}
      </button>
      {!collapsed && (
        <div className="space-y-0.5 border-t px-2 py-1">
          {segments
            ? segments.map((segment, index) => (
                <div key={`segment-${index}-${segment.owner ?? ''}`}>
                  {/* Which embedded SOP the next steps came from. `sop_ref` dissolves a child into
                      the parent's plan, so without this a composed replay reads as one flat list. */}
                  <div className="flex min-w-0 items-center gap-1.5 pt-1 pb-0.5 text-[11px] font-medium text-muted-foreground">
                    <ListChecksIcon className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="min-w-0 truncate">
                      {segment.owner ??
                        gt('This procedure', {
                          context:
                            'label for the composed-replay steps belonging to the parent procedure itself',
                        })}
                    </span>
                  </div>
                  {segment.steps.map((stepGroup) => (
                    <SopStepRow
                      key={`${stepGroup.step.stepId ?? stepGroup.step.index}`}
                      group={stepGroup}
                      gt={gt}
                      entryProps={entryProps}
                    />
                  ))}
                </div>
              ))
            : group.steps.map((stepGroup) => (
                <SopStepRow
                  key={`${stepGroup.step.stepId ?? stepGroup.step.index}`}
                  group={stepGroup}
                  gt={gt}
                  entryProps={entryProps}
                />
              ))}
          {group.runningStep && (
            // The step mid-replay: no mode or verify yet, so just its identity + a spinner. Its
            // completion replaces this row with the resolved one.
            <div className="flex min-w-0 items-center gap-2 py-1 text-[13px]">
              <span className="w-5 shrink-0" />
              <span className="w-5 shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums">
                {group.runningStep.index}
              </span>
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                {group.runningStep.name}
              </span>
              <CircleNotchIcon
                className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground"
                aria-hidden
              />
              <Badge variant="outline" className="shrink-0 text-[10px]">
                <T context="badge on the procedure step currently replaying">
                  running
                </T>
              </Badge>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The label + icon for an executor phase.
 *
 * Keys are the backend's `TracePhase` values (core/services/operator/tracePhase.ts). An unknown key
 * falls back to a generic label rather than rendering the raw id — a newer backend must never leak
 * `remote_desktop_login` into the UI as prose.
 */
function phaseChrome(
  phase: string,
  gt: ReturnType<typeof useGT>,
): { label: string; Icon: typeof MonitorIcon } {
  switch (phase) {
    case 'remote_desktop':
      return {
        label: gt('Remote desktop', {
          context: 'trajectory group: the agent driving the remote desktop',
        }),
        Icon: MonitorIcon,
      };
    case 'remote_desktop_login':
      return {
        label: gt('Remote desktop sign-in', {
          context: 'trajectory group: getting into the remote desktop',
        }),
        Icon: SignInIcon,
      };
    case 'web_browser':
      return {
        label: gt('Web browser', {
          context: 'trajectory group: the agent driving a web browser',
        }),
        Icon: GlobeIcon,
      };
    case 'sop_interview':
      return {
        label: gt('Teaching', {
          context: 'trajectory group: the procedure-authoring interview',
        }),
        Icon: GraduationCapIcon,
      };
    default:
      return {
        label: gt('Agent actions', {
          context: 'trajectory group: fallback for an unrecognized executor',
        }),
        Icon: CursorClickIcon,
      };
  }
}

/**
 * One executor's run of work — the peer of `ProcedureGroupCard` for everything that isn't a
 * procedure replay. Same envelope deliberately: the two read as siblings because that's what they
 * are, two executors that took turns driving one session.
 *
 * Expanded by default. Unlike a clean deterministic procedure step, agentic work has no summary
 * that stands in for it — the reasoning IS the content, and hiding it behind a caret would make the
 * common case worse to read, not better.
 */
function PhaseGroupCard({
  group,
  gt,
  entryProps,
}: {
  group: PhaseGroup;
  gt: ReturnType<typeof useGT>;
  entryProps: (message: SessionMessageEntry) => React.ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const { label, Icon } = phaseChrome(group.phase, gt);
  // Actions, not entries: roughly half the rows are the reasoning attached to an action, so
  // `messages.length` would report a run of six clicks as "12 actions".
  const actions = group.messages.filter((m) => Boolean(m.action)).length;

  return (
    <div className="rounded-md border bg-muted/20">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
        className="flex w-full min-w-0 items-center gap-2 px-2 py-1.5 text-left text-[13px] hover:bg-muted/40"
      >
        {collapsed ? (
          <CaretRightIcon className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <CaretDownIcon className="h-3.5 w-3.5 shrink-0" />
        )}
        <Icon
          weight="bold"
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
        <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
          <T context="summary of how many actions an executor phase performed">
            <Var>{actions}</Var> actions
          </T>
        </span>
      </button>
      {!collapsed && (
        <div className="space-y-1 border-t px-2 py-1">
          {group.messages.map((message) => entryProps(message))}
        </div>
      )}
    </div>
  );
}

/** One trace entry with its screenshot link and selection highlight resolved. Its own component so
 * the thread body stays a plain walk over the groups. */
function ThreadEntry({
  message,
  gt,
  screenshotMessageIds,
  onSelectScreenshot,
  selectedMessageId,
  showActionDetail,
}: {
  message: SessionMessageEntry;
  gt: ReturnType<typeof useGT>;
  screenshotMessageIds?: Map<string, number>;
  onSelectScreenshot?: (messageId: string) => void;
  selectedMessageId?: string;
  showActionDetail: boolean;
}) {
  const messageId = message.messageId;
  const reveal =
    messageId && screenshotMessageIds?.has(messageId) && onSelectScreenshot
      ? () => onSelectScreenshot(messageId)
      : undefined;
  return (
    <AgentThreadEntry
      message={message}
      gt={gt}
      onSelectScreenshot={reveal}
      isSelected={Boolean(messageId) && messageId === selectedMessageId}
      showDetail={showActionDetail}
    />
  );
}

/** The thread body: ungrouped entries in place, procedure replays as collapsible groups. Its own
 * component so AgentMessageThread stays about layout and scroll behaviour. */
function ThreadGroups({
  groups,
  gt,
  renderEntry,
}: {
  groups: TraceGroup[];
  gt: ReturnType<typeof useGT>;
  renderEntry: (message: SessionMessageEntry) => React.ReactNode;
}) {
  return (
    <>
      {groups.map((entry, index) => {
        if (entry.kind === 'message') return renderEntry(entry.message);
        if (entry.kind === 'phase') {
          return (
            <PhaseGroupCard
              // The same phase can legitimately open twice (a procedure replay between two runs of
              // desktop work), so the position is part of the identity.
              key={`phase-${index}-${entry.phase}`}
              group={entry}
              gt={gt}
              entryProps={renderEntry}
            />
          );
        }
        return (
          <ProcedureGroupCard
            key={`procedure-${entry.ordinal}-${entry.name ?? ''}`}
            group={entry}
            gt={gt}
            entryProps={renderEntry}
          />
        );
      })}
    </>
  );
}

function AgentMessageThread({
  messages,
  maxHeight,
  screenshotMessageIds,
  onSelectScreenshot,
  selectedMessageId,
  finalResponse,
  showActionDetail = false,
  followLatest = true,
  sopSteps,
  runningStep,
}: {
  messages: SessionMessageEntry[];
  maxHeight: number | null;
  /** messageIds that have a screenshot the user can reveal by clicking the entry. */
  screenshotMessageIds?: Map<string, number>;
  onSelectScreenshot?: (messageId: string) => void;
  /** The entry whose screenshot is currently selected (screenshot → message link). */
  selectedMessageId?: string;
  /** The operator's final summary, rendered as the closing entry below the steps. */
  finalResponse?: string | null;
  /** Show each entry's per-action detail badge (flag-gated by the caller). */
  showActionDetail?: boolean;
  /**
   * Live threads follow the newest message; replay threads (a static, already
   * complete list driven by the user's selection) should stay where they are.
   */
  followLatest?: boolean;
  /**
   * The SOP steps this run replayed. Supplied → the thread groups each step's actions beneath it
   * (procedure → step → actions) instead of listing them flat. Absent/empty and with no
   * step-tagged messages, the thread renders exactly as it always has.
   */
  sopSteps?: SopReplayStepView[];
  /** The step currently mid-replay, drawn as a spinner row so progress shows from step start. */
  runningStep?: SopReplayRunningStep | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Whether the user is parked at the bottom. We only auto-follow new messages
  // when they are — scrolling up to read history must not get yanked back down.
  const pinnedToBottomRef = useRef(true);
  const gt = useGT();

  // Procedure → step → actions. With no replay in the run this is one `message` entry per message,
  // so the render below is identical to the flat list it replaced. Not memoized: React Compiler
  // caches it, and a manual useMemo here is dead weight (react-doctor).
  const groups = groupTraceBySopStep(messages, sopSteps ?? [], runningStep);

  // One entry renderer, used at the top level and inside a step group, so a nested action keeps
  // its screenshot link and selection highlight.
  function renderEntry(message: SessionMessageEntry) {
    return (
      <ThreadEntry
        key={message.id}
        message={message}
        gt={gt}
        screenshotMessageIds={screenshotMessageIds}
        onSelectScreenshot={onSelectScreenshot}
        selectedMessageId={selectedMessageId}
        showActionDetail={showActionDetail}
      />
    );
  }

  useEffect(() => {
    if (!followLatest) return;
    const viewport = getScrollViewport(containerRef.current);
    if (!viewport) return;
    const handleScroll = () => {
      const distanceFromBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      pinnedToBottomRef.current = distanceFromBottom < 24;
    };
    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [followLatest]);

  // Follow the latest message only when pinned, and scroll the viewport itself
  // rather than scrollIntoView (which also scrolls the page and made the whole
  // layout jump on every poll).
  useEffect(() => {
    if (!followLatest || !pinnedToBottomRef.current) return;
    const viewport = getScrollViewport(containerRef.current);
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [messages.length, followLatest]);

  return (
    <div
      ref={containerRef}
      className="flex min-h-64 flex-col overflow-hidden rounded border bg-background"
      style={maxHeight ? { height: maxHeight } : undefined}
    >
      <div className="border-b px-3 py-2">
        <h3 className="text-sm font-medium">
          <T context="heading for the agent action/reasoning trajectory thread">
            Trajectory
          </T>
        </h3>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-1.5 p-3">
          {groups.length > 0 ? (
            <ThreadGroups groups={groups} gt={gt} renderEntry={renderEntry} />
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              <T context="empty state for live agent reasoning message thread">
                Reasoning will appear as the agent acts.
              </T>
            </p>
          )}
          {finalResponse?.trim() && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5">
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-primary">
                <CheckCircleIcon weight="fill" className="h-3.5 w-3.5" />
                <T context="label for the operator's final summary response in the thread">
                  Final response
                </T>
              </div>
              <ChatMarkdown className="text-foreground [&_p]:leading-5">
                {finalResponse}
              </ChatMarkdown>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionOverlayImage — renders an <img> with a red dot / action label overlay
// ---------------------------------------------------------------------------

function ActionOverlayImage({
  src,
  alt,
  className,
  imgClassName,
  coordinate,
  actionType,
  actionText,
  onClick,
}: {
  src: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  coordinate?: [number, number];
  actionType?: string;
  actionText?: string;
  onClick?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState<VideoScale | null>(null);

  const updateScale = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;
    setScale(
      computeContainedScale(
        img.clientWidth,
        img.clientHeight,
        img.naturalWidth,
        img.naturalHeight,
      ),
    );
  }, []);

  useResizeObserver(containerRef, updateScale);

  const showDot = coordinate && isClickAction(actionType);
  const showLabel = actionType && !isClickAction(actionType);

  return (
    <div
      ref={containerRef}
      className={cn('relative', className)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={imgClassName}
        onLoad={updateScale}
      />
      {showDot && scale && (
        <ClickRipple x={coordinate[0]} y={coordinate[1]} scale={scale} />
      )}
      {showLabel && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 pointer-events-none">
          <span className="text-xs text-white">
            {formatActionLabel(actionType, actionText)}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecordingPlayer — video with action overlay + timeline scrubber
// ---------------------------------------------------------------------------

function RecordingPlayer({
  src,
  toolCalls,
  recordingStoppedAt,
  sessionCompletedAt,
  onActiveActionChange,
}: {
  src: string;
  toolCalls: ToolCallRunResponse[];
  /**
   * True end of the recording MP4 (WF-77) — the instant the replay was stopped. Preferred over
   * sessionCompletedAt: completion happens before teardown stops the recording, so anchoring to
   * it shifts every marker right by the completion→stop gap. Null → fall back to completion.
   */
  recordingStoppedAt: Date | null;
  sessionCompletedAt: Date | null;
  /** Bubbles the action that's current at the playback position (→ thread sync). */
  onActiveActionChange?: (id: string | null) => void;
}) {
  const gt = useGT();

  // Build action markers from tool calls. Only entries with a real action
  // (not bare screenshots) are surfaced as markers / skip-inactivity anchors.
  const actions = useMemo<VideoActionMarker[]>(() => {
    return toolCalls
      .map((tc): VideoActionMarker | null => {
        if (!tc.startedAt) return null;
        const { coordinate, action, text } = extractActionData(
          tc.inputData,
          tc.outputData as Record<string, unknown> | null,
        );
        if (!action) return null;
        return {
          id: tc.id,
          timestampMs: new Date(tc.startedAt).getTime(),
          label: gt('{action} at action marker', {
            context:
              'tooltip / aria-label for recording timeline action marker',
            action:
              formatActionLabel(action, text) ||
              gt('Action', { context: 'fallback action marker label' }),
          }),
          coordinate,
          actionType: action,
          actionText: text,
        };
      })
      .filter((m): m is VideoActionMarker => m !== null);
  }, [toolCalls, gt]);

  // The recording's true end. Prefer the persisted stop time (exact MP4 end); fall back to the
  // session completion time, then to estimating from the last tool-call timestamp. Only the first
  // is exact — the fallbacks precede the real stop, so markers drift (see WF-77 / the prop docs).
  const recordingEndsAtMs = useMemo(() => {
    if (recordingStoppedAt) return recordingStoppedAt.getTime();
    if (sessionCompletedAt) return sessionCompletedAt.getTime();
    const timestamps = toolCalls
      .map((tc) => (tc.startedAt ? new Date(tc.startedAt).getTime() : 0))
      .filter((t) => t > 0);
    return timestamps.length > 0 ? Math.max(...timestamps) + 5000 : null;
  }, [recordingStoppedAt, sessionCompletedAt, toolCalls]);

  return (
    <SessionVideoPlayer
      src={src}
      recordingEndsAtMs={recordingEndsAtMs}
      actions={actions}
      showActionOverlay
      onActiveActionChange={onActiveActionChange}
    />
  );
}

// ---------------------------------------------------------------------------
// Live-activity accumulation — the union of every poll/stream payload seen for
// the current run. In orchestrated runs the setup workflow (login) and the
// live operator can emit into DIFFERENT replicas' copies of the same session
// (the cross-worker reattach re-registers it with fresh events), so any single
// payload may carry only one phase's trajectory and consecutive polls can
// alternate between the two. Keeping the union (sorted by time) renders setup
// and live as one continuous thread/gallery instead of swapping, and makes a
// transient empty payload harmless.
// ---------------------------------------------------------------------------

/** Dedup key for a thread entry across payloads/sources. `messageId` is the
 * durable A2A id every source carries; legacy events (no messageId) dedupe on
 * their content instead. */
function sessionMessageKey(m: SessionMessageEntry): string {
  return (
    m.messageId ??
    `${m.timestamp}|${m.action ?? ''}|${(m.message ?? '').slice(0, 80)}`
  );
}

/** Dedup key for a screenshot frame. Several frames can ride one message
 * (same messageId + timestamp), so the frame bytes/URL disambiguate — by TAIL,
 * not head: base64 PNGs share a long common prefix. */
function screenshotKey(s: ScreenshotEntry): string {
  return `${s.messageId ?? ''}|${s.timestamp}|${s.data.length}:${s.data.slice(-64)}`;
}

/**
 * Fold every source payload into the accumulator map and return the entries
 * sorted by timestamp. The sorted view is cached in `viewRef` and reused when
 * nothing changed, so downstream memos keep a stable identity across polls.
 * `contentOf` detects in-place growth (streamed reasoning text) so the updated
 * entry replaces the stale one instead of duplicating it.
 */
function accumulateEntries<T extends { timestamp: number }>(
  acc: Map<string, T>,
  viewRef: { current: T[] },
  sources: (T[] | undefined)[],
  keyOf: (entry: T) => string,
  contentOf: (entry: T) => string,
): T[] {
  let changed = false;
  for (const source of sources) {
    for (const entry of source ?? []) {
      const key = keyOf(entry);
      const existing = acc.get(key);
      if (!existing || contentOf(existing) !== contentOf(entry)) {
        acc.set(key, entry);
        changed = true;
      }
    }
  }
  if (changed || viewRef.current.length !== acc.size) {
    // Stable sort: ties keep first-seen (per-source chronological) order.
    viewRef.current = [...acc.values()].sort(
      (a, b) => a.timestamp - b.timestamp,
    );
  }
  return viewRef.current;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// Stable empty reference for the eval path (no live screenshots) so the
// liveItems memo doesn't see a fresh array every render.
const NO_SCREENSHOTS: ScreenshotEntry[] = [];

/**
 * View (live iframe / screenshot gallery) on the left, Trajectory on the right — so a wide surface
 * spends its extra width on the trace instead of letterboxing the view. Stacks when narrow, where
 * two columns would leave neither readable. Keyed off the CONTAINER (`@container` on the viewer
 * root), not the viewport: the viewer also renders inside the ~26rem chat sidebar of a wide window,
 * where a viewport breakpoint split into two columns whose 22rem Trajectory floor squeezed the view
 * column — and the live iframe in it — to a few invisible pixels. 48rem = the Trajectory floor plus
 * a view column at least as wide. `items-start`, not `stretch`: the Trajectory sizes itself from the
 * measured view height, and stretching would let it grow the view column and feed that back into
 * the measurement.
 */
const VIEW_WITH_TRAJECTORY_GRID =
  'grid items-start gap-3 @[48rem]:grid-cols-[minmax(0,2fr)_minmax(22rem,1fr)]';

// Switch ids hoisted to consts so i18next/no-literal-string (jsx-only) doesn't flag
// them when passed through interactiveToggle() in JSX (same quirk as entryProps()).
const LIVE_VIEW_INTERACTIVE_ID = 'live-view-interactive';
const LIVE_VIEW_INTERACTIVE_MAXIMIZED_ID = 'live-view-interactive-maximized';

/**
 * Caller-supplied data that drives the viewer in place of the live-session
 * hooks. Used by agent-evals, whose turns/screenshots come from the eval RPC
 * (there is no browser session to poll). When set, leave `sessionId`/
 * `organizationId` unset so the session/stream/document hooks stay inert and
 * these values feed the tabs directly.
 */
export interface ExternalSessionData {
  /** Session-status badge; null hides it (the eval page shows its own status). */
  status: AgentSessionStatus | null;
  isComplete: boolean;
  /** Live remote-desktop/browser URL (live runs); null on completed cases. */
  liveViewUrl: string | null;
  /** Thread entries — the operator turns, shown in the live + completed thread. */
  messages: SessionMessageEntry[];
  /** Gallery frames (completed cases); empty while live. */
  screenshotItems: ScreenshotItem[];
  /** Session-replay Document id → lights up the Recording tab; null when unrecorded. */
  recordingDocumentId?: string | null;
}

interface AgentSessionViewerProps {
  /** Identifies the run being viewed; switching it resets per-run UI state. */
  runId?: string | null;
  /** Explicit run lifecycle when known; overrides session/media-based inference. */
  runIsComplete?: boolean;
  /** Omitted when `externalData` drives the viewer (the hooks stay inert). */
  organizationId?: string;
  sessionId?: string | null;
  /** Pre-fetched from the timeline so the recording tab is available on load. */
  recordingDocumentId?: string | null;
  /** Gzipped Kernel telemetry archive associated with this run, when persisted. */
  kernelTelemetryDocumentId?: string | null;
  /** Summary from File.metadata so the Events tab can render without the blob. */
  kernelTelemetrySummary?: KernelTelemetrySummary | null;
  /** Persisted screenshots from the timeline, also used to focus live trace actions. */
  screenshotToolCalls?: ToolCallRunResponse[];
  /**
   * Screenshot captured before the first action (the frame the agent saw at
   * the start). Used to anchor the first click on its before-state.
   */
  initialScreenshotUrl?: string | null;
  /** When the agent run completed — used to align action overlays on the recording. */
  runCompletedAt?: string | Date | null;
  /** The operator's final summary (its `done` thought), shown as the closing entry. */
  finalResponse?: string | null;
  /** Drive the viewer from caller-supplied data instead of the session hooks. */
  externalData?: ExternalSessionData;
  /**
   * Focus a thread entry (and its screenshot) by its message or persisted tool-call
   * ID. A fresh object per request re-applies the focus.
   */
  focusRequest?: { messageId: string } | null;
  /**
   * Capture surface rendered ABSOLUTELY over the live-view iframe (same rect), on top of the
   * pointer-events blocker. The interview's annotate overlay (AI-246) plugs in here so its canvas
   * aligns to the iframe pixel-for-pixel. Absent everywhere else.
   */
  liveOverlay?: ReactNode;
  /**
   * Externally force live-view interactivity, overriding the built-in toggle (AI-246 takeover):
   * `true` passes pointer events into the iframe so the human drives the real Kernel browser even
   * on the `externalData` path. Leave undefined to keep the built-in (toggle, off for `ext`).
   */
  liveInteractive?: boolean;
  /**
   * Suppress the reasoning/action thread ("Trajectory") on EVERY tab, leaving just the live view,
   * the recording, or the screenshot gallery. The operator interview sets this because the
   * conversation lives in its own chat pane beside the viewer; the Agent Monitor sets it because
   * its Agent Reasoning rail is the same trajectory, wider and with the nested child runs.
   *
   * It once applied to Live Watch alone, which meant a *completed* run — the Recording and
   * Screenshots tabs — still drew a second, narrower copy of the thread beside the caller's own.
   */
  hideMessageThread?: boolean;
  /**
   * Bumping this value remounts the live-view iframe, forcing it to re-fetch the SAME URL. Used by
   * mock_ui.studio to reload the preview after the agent edits the mock and pauses for feedback:
   * the preview is a cross-origin signed URL, so we can't call `.reload()` on it, and mutating the
   * URL would break its signature — a React `key` change is the safe reload lever.
   */
  liveViewReloadToken?: string | number;
  /**
   * SOP steps this run replayed, for a run whose live session is gone — build them from the run
   * timeline with `sopStepsFromToolCalls`. Supplied → the trajectory groups each step's actions
   * beneath it. While a live stream is attached the viewer prefers that instead (it's fresher and
   * carries the in-progress step), so a caller can pass this unconditionally.
   */
  sopSteps?: SopReplayStepView[];
}

export function AgentSessionViewer({
  runId = null,
  runIsComplete,
  organizationId,
  sessionId,
  recordingDocumentId: recordingDocumentIdProp = null,
  kernelTelemetryDocumentId = null,
  kernelTelemetrySummary = null,
  screenshotToolCalls = [],
  sopSteps = [],
  initialScreenshotUrl = null,
  runCompletedAt: runCompletedAtProp = null,
  finalResponse = null,
  externalData,
  focusRequest = null,
  liveOverlay,
  liveInteractive,
  hideMessageThread = false,
  liveViewReloadToken,
}: AgentSessionViewerProps) {
  const gt = useGT();
  const STATUS_CONFIG = useStatusConfig();

  const sessionStatus = useSessionStatus({
    organizationId,
    sessionId: sessionId ?? undefined,
  });
  // Live operator stream over AG-UI. Inactive unless a sessionId is
  // present (i.e. during a live run); completed runs read the timeline. The poll
  // above stays as the fallback + lifecycle/recording-handoff driver.
  const operatorStream = useOperatorSessionStream({
    enabled: true,
    organizationId,
    sessionId: sessionId ?? undefined,
  });
  const getDocument = useGetDocument();
  const {
    mutate: fetchDocument,
    isPending: isFetchingDocument,
    isError: isDocumentError,
  } = getDocument;

  const [liveSelectedIndex, setLiveSelectedIndex] = useState<number | null>(
    null,
  );
  // Screenshot opened by clicking a thread message when the live surface is the
  // remote-desktop iframe (so there's no gallery to jump within). Null = closed.
  const [chipDialogSrc, setChipDialogSrc] = useState<string | null>(null);
  // The left ("view") column — the live iframe / recording player / screenshot gallery. Measured so
  // the Trajectory beside it matches its height instead of guessing. Only one tab is mounted at a
  // time (Radix unmounts the others), so one element serves all three. Held in STATE, not a ref:
  // useResizeObserver reads `ref.current` during render, so a ref would still point at the outgoing
  // tab's column on the render that swaps them and observe a detached node. A callback ref re-renders
  // on attach, which is exactly the signal the observer needs to resubscribe.
  const [viewColumnEl, setViewColumnEl] = useState<HTMLDivElement | null>(null);
  const [viewColumnHeight, setViewColumnHeight] = useState<number | null>(null);

  // Live thread/gallery = the accumulated UNION of the AG-UI stream and the
  // poll (not either/or): in an orchestrated run the setup workflow and the
  // live operator can live on different replicas, so each source may hold a
  // different slice of the trajectory (see accumulateEntries). When the caller
  // injects `externalData` (agent-evals), use that instead — the hooks above
  // are inert with no sessionId.
  const ext = externalData ?? null;
  const streamActivity = !operatorStream.unavailable
    ? operatorStream.activity
    : null;
  const liveScreenshotsAccRef = useRef<Map<string, ScreenshotEntry>>(new Map());
  const liveScreenshotsViewRef = useRef<ScreenshotEntry[]>([]);
  const liveMessagesAccRef = useRef<Map<string, SessionMessageEntry>>(
    new Map(),
  );
  const liveMessagesViewRef = useRef<SessionMessageEntry[]>([]);
  const liveScreenshots: ScreenshotEntry[] = ext
    ? NO_SCREENSHOTS
    : accumulateEntries(
        liveScreenshotsAccRef.current,
        liveScreenshotsViewRef,
        [streamActivity?.screenshots, sessionStatus.screenshots],
        screenshotKey,
        // Frames are immutable — only additions count as changes.
        () => '',
      );
  const liveMessages: SessionMessageEntry[] = ext
    ? ext.messages
    : accumulateEntries(
        liveMessagesAccRef.current,
        liveMessagesViewRef,
        [streamActivity?.messages, sessionStatus.messages],
        sessionMessageKey,
        (m) => m.message ?? '',
      );
  const liveStatus = ext
    ? ext.status
    : (streamActivity?.status ?? sessionStatus.status);
  // SOP replay step headers. Live they come off the stream this component already holds; once the
  // run is over the live session is gone, so the caller supplies them from the persisted trace
  // (`sopStepsFromToolCalls` over the run timeline). Live wins while it exists — it's the fresher
  // of the two, and carries the in-progress step.
  const liveReplaySteps = streamActivity?.sopReplay.steps ?? [];
  const replaySteps = liveReplaySteps.length > 0 ? liveReplaySteps : sopSteps;
  const replayRunningStep = streamActivity?.sopReplay.running ?? null;
  const hasLiveScreenshots = liveScreenshots.length > 0;
  const statusConfig = liveStatus ? STATUS_CONFIG[liveStatus] : null;
  const liveViewUrl = ext
    ? ext.liveViewUrl
    : (streamActivity?.liveViewUrl ?? sessionStatus.data?.liveViewUrl ?? null);
  const [interactiveEnabled, setInteractiveEnabled] = useState(false);
  // The live view opened in a large dialog (the inline pane is small in narrow surfaces
  // like the chat sidebar). Kernel live views tolerate concurrent watchers, so the
  // inline iframe stays mounted behind the dialog.
  const [liveViewExpanded, setLiveViewExpanded] = useState(false);
  // Eval live views are watch-only — no interactive toggle into the iframe. The interview's
  // takeover (AI-246) overrides this externally via `liveInteractive` to pass input through.
  const canInteractWithLiveView =
    liveInteractive ?? (interactiveEnabled && !ext);

  const recordingDocumentId = ext
    ? (ext.recordingDocumentId ?? null)
    : (recordingDocumentIdProp ?? sessionStatus.recordingDocumentId);
  // The owning run's lifecycle is authoritative, including while its live
  // session lookup is pending. Older callers retain their session/media fallback.
  const isComplete =
    ext?.isComplete ??
    runIsComplete ??
    (sessionStatus.isComplete ||
      (!sessionId &&
        (Boolean(recordingDocumentIdProp) || screenshotToolCalls.length > 0)));

  const recordingSignedUrl = getDocument.data?.data.signedUrl ?? null;

  // Normalise live screenshots into ScreenshotItem[]. `data` is usually raw
  // base64 (legacy/local), but the A2A operator sends a presigned blob URL (or
  // a data: URI) — use those verbatim as the <img src>.
  const liveItems = useMemo<ScreenshotItem[]>(
    () =>
      liveScreenshots.map((s, i) => ({
        id: `live-${s.timestamp}-${i}`,
        src: /^(https?:|data:)/.test(s.data)
          ? s.data
          : `data:image/png;base64,${s.data}`,
        label: formatActionName(s.action, gt),
        messageId: s.messageId,
      })),
    [liveScreenshots, gt],
  );

  // Normalise tool-call screenshots into ScreenshotItem[] (see
  // buildScreenshotItems for the before-frame vs 1:1 reasoning).
  const toolCallItems = useMemo<ScreenshotItem[]>(
    () =>
      buildScreenshotItems({
        toolCalls: screenshotToolCalls,
        initialScreenshotUrl,
        finalLabel: gt('Final state', {
          context: 'label for the final screenshot after the last agent action',
        }),
        initialLabel: gt('Initial state', {
          context: 'label for the screenshot before the first agent action',
        }),
      }),
    [screenshotToolCalls, initialScreenshotUrl, gt],
  );

  // The persisted message history (completed runs): one entry per step, sharing
  // the tool-call id with its screenshot (above). Action + reasoning + metadata
  // all come from the durable audit row, so this survives the live session.
  const toolCallMessages = useMemo<SessionMessageEntry[]>(
    () => buildToolCallMessages(screenshotToolCalls),
    [screenshotToolCalls],
  );

  const primaryTabValue: AgentSessionTab = defaultAgentSessionTab({
    isComplete,
    showRecording: Boolean(recordingDocumentId),
    showScreenshots: screenshotToolCalls.length > 0,
    showEvents: hasKernelTelemetry({
      kernelTelemetryDocumentId,
      kernelTelemetrySummary,
    }),
  });
  const { activeTab, selection, selectTab, selectScreenshot } =
    useAgentSessionNavigation({
      runId,
      focusRequest,
      defaultTab: primaryTabValue,
    });
  const screenshotItems = selectScreenshotItems({
    externalItems: ext?.screenshotItems,
    isComplete,
    liveItems,
    toolCallItems,
    selection,
  });
  // Completed-run thread: injected turns (evals) or the persisted tool-call steps.
  const completedMessages = ext ? ext.messages : toolCallMessages;

  // Preserve the last non-empty list so a transient empty/loading poll (e.g.
  // the running→complete handoff while the timeline refetches) never unmounts
  // the Screenshots tab or blanks the gallery mid-run.
  const lastNonEmptyItemsRef = useRef<ScreenshotItem[]>([]);
  if (screenshotItems.length > 0) {
    lastNonEmptyItemsRef.current = screenshotItems;
  }
  const stableScreenshotItems =
    screenshotItems.length > 0 ? screenshotItems : lastNonEmptyItemsRef.current;

  // Same sticky treatment for the live reasoning thread — it reads from the same
  // 500ms session poll, so a transient empty response shouldn't blank it and
  // flash it back.
  const lastNonEmptyMessagesRef = useRef<SessionMessageEntry[]>([]);
  if (liveMessages.length > 0) {
    lastNonEmptyMessagesRef.current = liveMessages;
  }
  const stableLiveMessages =
    liveMessages.length > 0 ? liveMessages : lastNonEmptyMessagesRef.current;

  // Resolve by identity on each poll so late captures and reordered rows do not
  // change the selected image. A cite can highlight a turn without a screenshot.
  const screenshotSelectedIndex = Math.max(
    0,
    stableScreenshotItems.findIndex((item) =>
      screenshotMatchesSelection(item, selection),
    ),
  );
  const focusedMessageId = selection?.kind === 'message' ? selection.id : null;
  // The step the recording is currently playing through — drives the thread on
  // the recording tab to follow along (highlight + scroll). See the TODO in
  // SessionVideoPlayer on the timestamp↔video alignment assumption.
  const [recordingActiveId, setRecordingActiveId] = useState<string | null>(
    null,
  );

  // When the viewer switches to a different run, drop the sticky/accumulated
  // caches and reset selection during render (not in an effect) so a new run
  // with no screenshots can't render the previous run's frames.
  const [trackedRunId, setTrackedRunId] = useState(runId);
  if (trackedRunId !== runId) {
    setTrackedRunId(runId);
    lastNonEmptyItemsRef.current = [];
    liveScreenshotsAccRef.current = new Map();
    liveScreenshotsViewRef.current = [];
    liveMessagesAccRef.current = new Map();
    liveMessagesViewRef.current = [];
    setLiveSelectedIndex(null);
    setLiveViewExpanded(false);
  }

  // Follow-latest for live screenshots
  const displayLiveIndex =
    liveSelectedIndex === null ? liveItems.length - 1 : liveSelectedIndex;
  // Follow the active frame in the live thumbnail strip (latest while streaming,
  // or whatever the user clicked) without scrolling the page.
  const {
    containerRef: liveThumbStripRef,
    activeThumbRef: liveActiveThumbRef,
  } = useKeepActiveThumbInView(displayLiveIndex, liveItems.length);

  // --- Determine which tabs to show ---
  const showLiveWatch = !isComplete && (Boolean(sessionId) || Boolean(ext));
  const showRecording = isComplete && Boolean(recordingDocumentId);
  // Evals may have turns but no screenshots (text-only/errored case) — still
  // surface the thread under the screenshots tab.
  const showScreenshots =
    stableScreenshotItems.length > 0 ||
    (Boolean(ext) && completedMessages.length > 0);
  const showEvents =
    hasKernelTelemetry({
      kernelTelemetryDocumentId,
      kernelTelemetrySummary,
    }) || showLiveWatch;
  const eventsNotable = notableKernelTelemetryCount(kernelTelemetrySummary);

  const resolvedTab: AgentSessionTab = resolveAgentSessionTab({
    activeTab,
    showLiveWatch,
    showRecording,
    showScreenshots,
    showEvents,
  });

  // Completion can display Recording as a fallback for Live Watch without
  // changing the user's stored tab. Load the media for the tab actually shown.
  useEffect(() => {
    if (
      resolvedTab === 'recording' &&
      recordingDocumentId &&
      !recordingSignedUrl &&
      !isFetchingDocument
    ) {
      fetchDocument({ documentId: recordingDocumentId });
    }
  }, [
    resolvedTab,
    recordingDocumentId,
    recordingSignedUrl,
    isFetchingDocument,
    fetchDocument,
  ]);

  useResizeObserver(viewColumnEl, (entry) => {
    const nextHeight = Math.ceil(entry.contentRect.height);
    if (nextHeight <= 0) return;

    setViewColumnHeight((currentHeight) =>
      currentHeight === nextHeight ? currentHeight : nextHeight,
    );
  });

  // Hide when there's nothing to show
  if (
    !shouldRenderAgentSessionViewer({
      showLiveWatch,
      showRecording,
      showScreenshots,
      showEvents,
    })
  )
    return null;

  function handleLiveThumbnailSelect(index: number) {
    if (index === liveItems.length - 1) {
      setLiveSelectedIndex(null);
    } else {
      setLiveSelectedIndex(index);
    }
  }

  // messageId → index of the frame captured on that thread message. The tie is
  // the durable A2A messageId carried on each screenshot (operator path).
  const liveIndexByMessageId = new Map<string, number>();
  liveItems.forEach((item, index) => {
    if (item.messageId) liveIndexByMessageId.set(item.messageId, index);
  });

  // Click a message → see its screenshot. When the live surface is the gallery,
  // jump to that frame; when it's the remote-desktop iframe, open it in a dialog.
  function handleSelectMessageScreenshot(messageId: string) {
    const index = liveIndexByMessageId.get(messageId);
    if (index === undefined) return;
    if (liveViewUrl) {
      setChipDialogSrc(liveItems[index].src);
    } else {
      setLiveSelectedIndex(index);
    }
  }

  // Completed view: gallery ↔ thread share the same selection. messageId is the
  // tool-call id carried on both the screenshot item and its thread entry.
  const screenshotIndexByMessageId = new Map<string, number>();
  stableScreenshotItems.forEach((item, index) => {
    if (item.messageId) screenshotIndexByMessageId.set(item.messageId, index);
  });
  const selectedMessageId =
    stableScreenshotItems[screenshotSelectedIndex]?.messageId;

  // User drives the gallery directly → drop any evidence-cite focus so the
  // highlight follows their selection again.
  function chooseScreenshot(index: number) {
    const item = stableScreenshotItems[index];
    if (item) selectScreenshot(item);
  }

  // Click a thread message → select its screenshot in the gallery.
  function handleSelectScreenshotByMessageId(messageId: string) {
    const index = screenshotIndexByMessageId.get(messageId);
    if (index !== undefined) chooseScreenshot(index);
  }

  // --- Tab content builders ---

  const liveViewSrc = liveViewUrl
    ? liveViewUrl.startsWith('http')
      ? liveViewUrl
      : `${config.apiBaseUrl}${liveViewUrl}`
    : null;

  // The Interactive switch, rendered inline and in the maximize dialog. Parameterised
  // by id because both can be mounted at once and htmlFor must stay unambiguous.
  function interactiveToggle(id: string) {
    return (
      <div className="mb-2 flex items-center gap-2">
        <Switch
          id={id}
          checked={interactiveEnabled}
          onCheckedChange={setInteractiveEnabled}
        />
        <Label htmlFor={id}>
          <T context="toggle to allow clicking into the live remote desktop iframe">
            Interactive
          </T>
        </Label>
      </div>
    );
  }

  // One live-view iframe, shared by the inline pane and the maximize dialog so the
  // two never drift. `reloadKey` remounts it → reloads the same URL (see
  // liveViewReloadToken); the dialog copy mounts fresh on open, so it needs none.
  function liveViewIframe(reloadKey?: number | string) {
    return (
      <iframe
        key={reloadKey}
        src={liveViewSrc ?? undefined}
        title={gt('Live desktop view', {
          context: 'iframe title for live remote desktop stream',
        })}
        className={cn(
          'w-full border-0',
          !canInteractWithLiveView && 'pointer-events-none',
        )}
        // 16:9 — the shape of the remote desktops and browser live views this streams. At
        // 4:3 the extra height was pure black letterbox around the same picture.
        style={{ aspectRatio: '16/9' }}
        allow="autoplay; clipboard-write"
        tabIndex={canInteractWithLiveView ? 0 : -1}
      />
    );
  }

  const liveWatchContent = (
    // No thread (the operator interview keeps the conversation in its own pane) → no second column
    // to reserve; the view takes the full width it always did.
    <div className={hideMessageThread ? 'min-w-0' : VIEW_WITH_TRAJECTORY_GRID}>
      <div ref={setViewColumnEl} className="min-w-0">
        {liveViewUrl ? (
          <>
            {!ext && interactiveToggle(LIVE_VIEW_INTERACTIVE_ID)}
            <div className="relative rounded border overflow-hidden bg-black">
              {liveViewIframe(liveViewReloadToken)}
              {!canInteractWithLiveView && !liveOverlay && (
                <div className="absolute inset-0 z-10" aria-hidden="true" />
              )}
              {liveOverlay && (
                <div className="absolute inset-0 z-20">{liveOverlay}</div>
              )}
              {/* Hidden while an interview overlay owns the surface — its own UI decides
                  what may cover the view. z-30 keeps the button clickable above the
                  non-interactive shield (z-10). */}
              {!liveOverlay && (
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => setLiveViewExpanded(true)}
                  aria-label={gt('Maximize live view', {
                    context:
                      'button that opens the live session view in a large dialog',
                  })}
                  className="absolute top-2 right-2 z-30 h-7 w-7 bg-black/50 text-white hover:bg-black/70 hover:text-white"
                >
                  <ArrowsOutSimpleIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
            {/* Maximized live view. Lives inside the live-watch tab, so completing the
                run (which drops the tab) closes it with everything else. An interview
                overlay taking the surface closes it too — the dialog's copy of the iframe
                carries no overlay, so leaving it up would hand the user an unmediated view
                of a surface the overlay is supposed to own. */}
            <Dialog
              open={liveViewExpanded && !liveOverlay}
              onOpenChange={setLiveViewExpanded}
            >
              <DialogContent className="!max-w-[min(95vw,80rem)] sm:!max-w-[min(95vw,80rem)] max-h-[95vh] overflow-y-auto p-4">
                <DialogHeader>
                  <DialogTitle>
                    <T context="dialog title for the maximized live session view">
                      Live view
                    </T>
                  </DialogTitle>
                </DialogHeader>
                {!ext && interactiveToggle(LIVE_VIEW_INTERACTIVE_MAXIMIZED_ID)}
                <div className="relative rounded border overflow-hidden bg-black">
                  {liveViewIframe()}
                  {!canInteractWithLiveView && (
                    <div className="absolute inset-0 z-10" aria-hidden="true" />
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </>
        ) : hasLiveScreenshots ? (
          <div className="flex gap-3 items-start relative">
            {liveItems.length > 1 && (
              <>
                <div className="w-32" />
                <div ref={liveThumbStripRef} className="w-32 absolute h-full">
                  <ScrollArea className="h-full">
                    <div className="flex flex-col gap-1.5 pr-2">
                      {liveItems.map((item, index) => {
                        const isActive = index === displayLiveIndex;
                        return (
                          <button
                            type="button"
                            key={item.id}
                            ref={isActive ? liveActiveThumbRef : null}
                            onClick={() => handleLiveThumbnailSelect(index)}
                            className={cn(
                              'relative rounded border overflow-hidden cursor-pointer transition-all',
                              'hover:ring-2 hover:ring-primary/50',
                              isActive
                                ? 'ring-2 ring-primary border-primary'
                                : 'border-border',
                            )}
                          >
                            <img
                              src={item.src}
                              alt={item.label}
                              className="w-full h-auto"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                              <span className="text-[9px] text-white truncate block">
                                {item.label}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              </>
            )}
            <div className="flex-1 min-w-0">
              {liveItems[displayLiveIndex] && (
                <div className="space-y-1">
                  <div className="rounded border overflow-hidden">
                    <img
                      src={liveItems[displayLiveIndex].src}
                      alt={gt('Current screen', {
                        context: 'alt text for agent live screenshot',
                      })}
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    {liveSelectedIndex === null ? (
                      <span className="flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                        <T context="indicator that live feed is active">Live</T>
                      </span>
                    ) : (
                      <>
                        <span>
                          {liveItems[displayLiveIndex].label} (
                          {displayLiveIndex + 1}/{liveItems.length})
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-2 text-xs"
                          onClick={() => setLiveSelectedIndex(null)}
                        >
                          <T context="button to resume following live screenshots">
                            Resume live
                          </T>
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <CircleNotchIcon className="h-5 w-5 animate-spin mr-2" />
            <T context="loading message while waiting for agent screenshots">
              Waiting for screenshots...
            </T>
          </div>
        )}
      </div>
      {!hideMessageThread && (
        <AgentMessageThread
          messages={stableLiveMessages}
          maxHeight={viewColumnHeight}
          screenshotMessageIds={liveIndexByMessageId}
          onSelectScreenshot={handleSelectMessageScreenshot}
          showActionDetail
          sopSteps={replaySteps}
          runningStep={replayRunningStep}
        />
      )}
    </div>
  );

  const sessionCompletedAt = sessionStatus.completedAt
    ? new Date(sessionStatus.completedAt)
    : runCompletedAtProp
      ? new Date(runCompletedAtProp)
      : null;

  // Exact recording end (WF-77) when the run carries a session (DB fallback resolves it for
  // completed runs). Null on the eval/external path → RecordingPlayer falls back to completion.
  const recordingStoppedAt = sessionStatus.recordingStoppedAt
    ? new Date(sessionStatus.recordingStoppedAt)
    : null;

  const recordingContent = (
    <>
      {recordingSignedUrl ? (
        // The step thread follows the recording — as it plays, the current step
        // highlights + scrolls into view (read-only).
        <div
          className={hideMessageThread ? 'min-w-0' : VIEW_WITH_TRAJECTORY_GRID}
        >
          <div ref={setViewColumnEl} className="min-w-0">
            <RecordingPlayer
              src={recordingSignedUrl}
              toolCalls={screenshotToolCalls}
              recordingStoppedAt={recordingStoppedAt}
              sessionCompletedAt={sessionCompletedAt}
              onActiveActionChange={setRecordingActiveId}
            />
          </div>
          {!hideMessageThread && toolCallMessages.length > 0 && (
            <AgentMessageThread
              messages={toolCallMessages}
              // Match the player's height, with the 320 this tab always had as the floor.
              maxHeight={Math.max(viewColumnHeight ?? 0, 320)}
              selectedMessageId={recordingActiveId ?? undefined}
              finalResponse={finalResponse}
              showActionDetail
              followLatest={false}
              sopSteps={replaySteps}
            />
          )}
        </div>
      ) : isDocumentError ? (
        <div className="rounded border overflow-hidden bg-black">
          <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
            <XCircleIcon className="h-6 w-6 text-destructive" />
            <T context="error message when session recording fails to load">
              Failed to load recording.
            </T>
            {recordingDocumentId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  fetchDocument({ documentId: recordingDocumentId })
                }
              >
                <T context="retry button for loading session recording">
                  Retry
                </T>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded border overflow-hidden bg-black">
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <CircleNotchIcon className="h-5 w-5 animate-spin mr-2" />
            <T context="loading message for session recording video">
              Loading recording...
            </T>
          </div>
        </div>
      )}
    </>
  );

  const screenshotsContent =
    stableScreenshotItems.length > 0 ? (
      completedMessages.length > 0 && !hideMessageThread ? (
        // Screenshots on the left, the persisted step thread (reasoning) to their
        // right — linked both ways: click a frame to highlight its message, or a
        // message to show its frame.
        <div className={VIEW_WITH_TRAJECTORY_GRID}>
          <div ref={setViewColumnEl} className="min-w-0">
            <ScreenshotGallery
              items={stableScreenshotItems}
              selectedIndex={screenshotSelectedIndex}
              onSelect={chooseScreenshot}
            />
          </div>
          <AgentMessageThread
            messages={completedMessages}
            // Match the gallery's height, but never shrink below the 420 this tab
            // always had — a run whose frames are short shouldn't squeeze the trace.
            maxHeight={Math.max(viewColumnHeight ?? 0, 420)}
            screenshotMessageIds={screenshotIndexByMessageId}
            onSelectScreenshot={handleSelectScreenshotByMessageId}
            selectedMessageId={focusedMessageId ?? selectedMessageId}
            finalResponse={finalResponse}
            showActionDetail
            followLatest={false}
            sopSteps={replaySteps}
          />
        </div>
      ) : (
        <ScreenshotGallery
          items={stableScreenshotItems}
          selectedIndex={screenshotSelectedIndex}
          onSelect={chooseScreenshot}
        />
      )
    ) : completedMessages.length > 0 && !hideMessageThread ? (
      // Eval case with turns but no screenshots — show the thread alone.
      <AgentMessageThread
        messages={completedMessages}
        maxHeight={420}
        selectedMessageId={focusedMessageId ?? undefined}
        finalResponse={finalResponse}
        showActionDetail
        sopSteps={replaySteps}
      />
    ) : (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <T context="empty state for agent screenshots tab">
          No screenshots available.
        </T>
      </div>
    );

  return (
    // @container: the view/Trajectory split keys off the viewer's own width — it renders both on
    // full pages and in the chat sidebar, where the viewport says nothing about the space it has.
    <div className="@container p-3">
      <div className="flex items-center gap-2 mb-2">
        {statusConfig && (
          <Badge
            className={cn(
              'flex items-center gap-1',
              statusConfig.color,
              'text-white',
            )}
          >
            {statusConfig.icon}
            {statusConfig.label}
          </Badge>
        )}
      </div>
      <Tabs value={resolvedTab} onValueChange={selectTab}>
        <TabsList>
          {showLiveWatch && (
            <TabsTrigger value="live-watch">
              <T context="tab for live remote desktop view">Live Watch</T>
            </TabsTrigger>
          )}
          {showRecording && (
            <TabsTrigger value="recording">
              <T context="tab for session screen recording video">Recording</T>
            </TabsTrigger>
          )}
          {showScreenshots && (
            <TabsTrigger value="screenshots">
              {ext && !isComplete ? (
                // Live: screenshots aren't streamed (Live Watch is the visual), so
                // this tab shows only the turn thread — label it honestly.
                <T context="tab for the live agent trajectory (screenshots not yet available)">
                  Trajectory
                </T>
              ) : (
                <T context="tab for agent session screenshots gallery">
                  Screenshots
                </T>
              )}
            </TabsTrigger>
          )}
          {showEvents && (
            <TabsTrigger value="events">
              {eventsNotable > 0 ? (
                <T context="tab for kernel browser telemetry events with notable count">
                  Events <Var>{eventsNotable}</Var>
                </T>
              ) : (
                <T context="tab for kernel browser telemetry events">Events</T>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {showLiveWatch && (
          <TabsContent value="live-watch" className="min-h-0">
            {liveWatchContent}
          </TabsContent>
        )}
        {showRecording && (
          <TabsContent value="recording">{recordingContent}</TabsContent>
        )}
        {showScreenshots && (
          <TabsContent value="screenshots">{screenshotsContent}</TabsContent>
        )}
        {showEvents && (
          <TabsContent value="events">
            <KernelTelemetryEventsPanel
              agentRunId={runId}
              live={showLiveWatch}
              summary={kernelTelemetrySummary}
              enabled={resolvedTab === 'events'}
            />
          </TabsContent>
        )}
      </Tabs>

      {sessionStatus.error && (
        <div className="mt-3 rounded bg-red-50 border border-red-200 p-2 text-sm text-red-700">
          {sessionStatus.error}
        </div>
      )}

      {/* Screenshot revealed by clicking a thread message while the live iframe
          occupies the main pane (otherwise the click jumps the gallery inline). */}
      <Dialog
        open={chipDialogSrc !== null}
        onOpenChange={(open) => !open && setChipDialogSrc(null)}
      >
        <DialogContent className="!max-w-[min(95vw,72rem)] sm:!max-w-[min(95vw,72rem)] max-h-[95vh] p-4">
          <DialogHeader>
            <DialogTitle>
              <T context="dialog title for a screenshot opened from the agent thread">
                Screenshot for this step
              </T>
            </DialogTitle>
          </DialogHeader>
          {chipDialogSrc && (
            <div className="max-h-[80vh] overflow-y-auto rounded border">
              <img
                src={chipDialogSrc}
                alt={gt('Screenshot for the selected agent step', {
                  context: 'alt text for screenshot opened from agent thread',
                })}
                className="w-full h-auto"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
